/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the three variant edits that are commands rather
   than typing (Masters Ladder R3).

   Every one is a single ProseMirror transaction on the SAME document
   the user is typing in, which is what puts hide / restore / revert on
   the one Ctrl+Z timeline (§2.7) alongside their keystrokes. None of
   them writes a layer directly: they change the document, and
   `deriveLayer()` reads the result. One brain, one direction.
   ═══════════════════════════════════════════════════════════════ */

import { TextSelection } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import type { Node as PMNode, Schema } from '@tiptap/pm/model';
import type { ContentBlock } from '../../shared/types';
import { blocksToDoc, newBlockId } from '../designer/schema';
import { BLOCK_NODE_NAMES, deleteSelectedBlocks, selectedBlocks } from '../designer/commands';
import { blockSelectKey } from './blockSelect';
import { GHOST_KEY } from './variantOps';

type Dispatch = ((tr: Transaction) => void) | undefined;

/** Find a top-level block by id. */
export function findBlock(
  state: EditorState,
  blockId: string
): { node: PMNode; pos: number } | null {
  let found: { node: PMNode; pos: number } | null = null;
  state.doc.forEach((node, pos) => {
    if (found) return;
    if ((node.attrs.blockId as string) === blockId) found = { node, pos };
  });
  return found;
}

export function isGhostBlock(state: EditorState, blockId: string): boolean {
  const hit = findBlock(state, blockId);
  if (!hit) return false;
  return (hit.node.attrs.meta as Record<string, unknown> | undefined)?.[GHOST_KEY] === true;
}

/**
 * Hide (or restore) a block in this variant. The block never leaves the
 * document — it becomes the dashed stub §2.4 requires, so "restore" is one
 * click and one undo away.
 */
export function setBlockGhost(
  state: EditorState,
  dispatch: Dispatch,
  blockId: string,
  ghost: boolean
): boolean {
  const hit = findBlock(state, blockId);
  if (!hit) return false;
  const meta = { ...((hit.node.attrs.meta as Record<string, unknown>) || {}) };
  if (ghost) meta[GHOST_KEY] = true;
  else delete meta[GHOST_KEY];
  if (!!(hit.node.attrs.meta as Record<string, unknown>)?.[GHOST_KEY] === ghost) return false;
  const tr = state.tr.setNodeAttribute(hit.pos, 'meta', meta);
  if (dispatch) dispatch(tr);
  return true;
}

/** The `meta` a ghosted block carries — the one place the hide flag is set,
 *  shared by the explicit "Hide" command below and the delete/hide switch
 *  (T4) so the two can never drift on what "hidden" means. */
function ghostMeta(node: PMNode): Record<string, unknown> {
  const meta = { ...((node.attrs.meta as Record<string, unknown>) || {}) };
  meta[GHOST_KEY] = true;
  return meta;
}

/** Hide every baseline block the selection touches; a layer-added block has
 *  nothing to hide behind, so it is left to the ordinary delete. */
export function hideSelectedBlocks(
  state: EditorState,
  dispatch: Dispatch,
  isBaseline: (id: string) => boolean
): boolean {
  const { from, to } = state.selection;
  const targets: number[] = [];
  state.doc.forEach((node, pos) => {
    const end = pos + node.nodeSize;
    if (end <= from || pos >= to) {
      if (!(from === to && from >= pos && from <= end)) return;
    }
    const id = (node.attrs.blockId as string) || '';
    if (!id || !isBaseline(id)) return;
    if ((node.attrs.meta as Record<string, unknown> | undefined)?.[GHOST_KEY] === true) return;
    targets.push(pos);
  });
  if (!targets.length) return false;
  let tr: Transaction | null = null;
  for (const pos of targets) {
    const node = state.doc.nodeAt(pos);
    if (!node) continue;
    tr = (tr || state.tr).setNodeAttribute(pos, 'meta', ghostMeta(node));
  }
  if (!tr) return false;
  if (dispatch) dispatch(tr);
  return true;
}

/** The top-level blocks in a document, in order — used below to find the
 *  caret's landing block after a delete/hide run. `caretAtBlockStart` in
 *  `designer/commands.ts` does the identical job for the plain delete, but is
 *  private to that file, so the small helper is repeated here rather than
 *  exported for one caller. */
function blockOffsets(doc: PMNode): number[] {
  const out: number[] = [];
  doc.forEach((node, offset) => {
    if (BLOCK_NODE_NAMES.includes(node.type.name)) out.push(offset);
  });
  return out;
}

function isGhostNode(node: PMNode | null | undefined): boolean {
  return (node?.attrs.meta as Record<string, unknown> | undefined)?.[GHOST_KEY] === true;
}

/** An empty, ownerless paragraph — what a spec falls back to when a delete
 *  would otherwise leave nothing to put a caret in. Mirrors
 *  `designer/commands.ts`'s private `emptyParagraph` (not exported, so
 *  repeated here — same reasoning as `blockOffsets` below). */
function emptyParagraph(schema: Schema): PMNode {
  return schema.nodes.specParagraph.create({
    blockId: newBlockId(),
    level: 1,
    continuation: false,
    excluded: false,
    meta: {},
  });
}

/** A caret at the start of the first NON-GHOST block at or after `pos`,
 *  falling back to the nearest non-ghost block BEFORE it. A ghost stub is
 *  `contenteditable="false"` (T4) — a caret parked inside one is a caret
 *  nothing can type into, so hiding a block must never leave the caret
 *  there. Only when every block in the document is a ghost (nothing live
 *  to land on) does this fall back to the plain "next block, else last"
 *  rule `designer/commands.ts`'s `caretAtBlockStart` uses. */
function caretAtBlockStart(doc: PMNode, pos: number) {
  const offsets = blockOffsets(doc);
  const live = offsets.filter((o) => !isGhostNode(doc.nodeAt(o)));
  const at =
    live.find((o) => o >= pos) ??
    [...live].reverse().find((o) => o < pos) ??
    offsets.find((o) => o >= pos) ??
    offsets[offsets.length - 1] ??
    0;
  return TextSelection.near(doc.resolve(Math.min(at + 1, doc.content.size)), 1);
}

/**
 * Delete / Backspace on a handle selection INSIDE A VARIANT (Table Standard
 * Tranche 4, T4): a baseline (office-master) block in the selection cannot be
 * REMOVED — the variant gate would refuse that, same as it always has — so it
 * is HIDDEN instead, exactly what "Hide in this variant" already does. A
 * block the variant's own layer added has nothing to hide behind, so it is
 * deleted like it always was. A mixed selection does both in ONE transaction,
 * so one Ctrl+Z restores every block the run touched, ghosted or removed
 * alike. An already-ghost baseline block is left exactly as it is.
 *
 * `isBaseline` null means "not in a variant" (Office Master, or no variant at
 * all) — this is then exactly `deleteSelectedBlocks`, untouched.
 */
export function deleteOrHideSelectedBlocks(
  state: EditorState,
  dispatch: Dispatch,
  isBaseline: ((id: string) => boolean) | null
): boolean {
  if (!isBaseline) return deleteSelectedBlocks(state, dispatch);
  const sel = selectedBlocks(state);
  if (!sel.length) return false;

  const first = sel[0];
  const isBaselineBlock = (node: PMNode): boolean => {
    const id = (node.attrs.blockId as string) || '';
    return !!id && isBaseline(id);
  };
  // Only the blocks that will actually be DELETED count toward "the whole
  // document went away" — a block that gets ghosted instead stays in the
  // doc, so the document is never really empty while one remains.
  const toDelete = sel.filter(({ node }) => !isBaselineBlock(node));
  const totalBlocks = blockOffsets(state.doc).length;

  let tr = state.tr;
  let changed = false;

  if (toDelete.length && toDelete.length === totalBlocks) {
    // Every block in the document is being deleted and none is hidden — the
    // same "whole document" case `deleteSelectedBlocks` guards against: a
    // spec always has somewhere to put the caret, so one empty paragraph
    // takes the document's place instead of leaving it empty.
    tr = tr.replaceWith(0, state.doc.content.size, emptyParagraph(state.schema));
    changed = true;
  } else {
    // Last first, so earlier positions in `sel` stay valid across the
    // deletes — the same rule `deleteSelectedBlocks` runs by.
    for (let i = sel.length - 1; i >= 0; i--) {
      const { node, pos } = sel[i];
      if (isBaselineBlock(node)) {
        if ((node.attrs.meta as Record<string, unknown> | undefined)?.[GHOST_KEY] === true)
          continue;
        tr = tr.setNodeAttribute(pos, 'meta', ghostMeta(node));
        changed = true;
      } else {
        tr = tr.delete(pos, pos + node.nodeSize);
        changed = true;
      }
    }
  }
  // Every touched block was already hidden — nothing to do, but the key is
  // still swallowed so the browser's own Backspace/Delete never runs on top
  // of a gutter selection.
  if (!changed) return true;

  // The gutter selection is explicit state, not text — it must not keep
  // painting a block that just got hidden or removed. Cleared on the SAME
  // transaction so it undoes with everything else, one Ctrl+Z at a time.
  tr.setMeta(blockSelectKey, { ids: [], anchor: null });
  tr.setSelection(caretAtBlockStart(tr.doc, tr.mapping.map(first.pos, -1)));
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

/**
 * Put the office master's words back. The override is not deleted here —
 * the block simply matches core again, and the derive drops it by
 * construction. That is what makes "revert" survive an undo: Ctrl+Z restores
 * the client's text and the override comes straight back with it.
 */
export function revertBlockToCore(
  state: EditorState,
  dispatch: Dispatch,
  blockId: string,
  coreBlock: ContentBlock
): boolean {
  const hit = findBlock(state, blockId);
  if (!hit) return false;
  const json = blocksToDoc([coreBlock]).content?.[0];
  if (!json) return false;
  // Keep whatever the VARIANT owns on the node (the ghost flag) and take
  // everything else from core.
  const ghost = (hit.node.attrs.meta as Record<string, unknown> | undefined)?.[GHOST_KEY];
  const attrs = { ...((json.attrs as Record<string, unknown>) || {}) };
  if (ghost) {
    attrs.meta = { ...((attrs.meta as Record<string, unknown>) || {}), [GHOST_KEY]: true };
  }
  const node = state.schema.nodeFromJSON({ ...json, attrs });
  const tr = state.tr.replaceWith(hit.pos, hit.pos + hit.node.nodeSize, node);
  const caret = Math.min(hit.pos + 1, tr.doc.content.size);
  tr.setSelection(TextSelection.near(tr.doc.resolve(caret)));
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}
